import type { DocSummary } from "@xop/dashboard-shared";

/** Pretty file size — KB, MB, GB. Mirrors the customer-support helper
 * so a 1024 byte file shows up as "1.0 KB" in both tools. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Compare two version strings ("9.10" vs "9.2") as a tuple of numbers
 * rather than lexicographically — so 9.10 sorts above 9.2. Non-numeric
 * components fall back to 0. */
export function compareVersions(a: string, b: string): number {
  const parts = (s: string): number[] =>
    s.split(/[.\-_]/).map((p) => {
      const n = parseInt(p, 10);
      return Number.isNaN(n) ? 0 : n;
    });
  const av = parts(a);
  const bv = parts(b);
  const len = Math.max(av.length, bv.length);
  for (let i = 0; i < len; i++) {
    const ai = av[i] ?? 0;
    const bi = bv[i] ?? 0;
    if (ai !== bi) return ai - bi;
  }
  return 0;
}

export interface ReleaseGroup {
  /** Release version string ("9.2") or null for the "Unversioned" bucket. */
  release: string | null;
  /** Optional release name ("Storm") taken from the first doc in the group. */
  name: string | null;
  docs: DocSummary[];
}

/** Group a flat list of docs into sections keyed by release version.
 *
 * Releases come back in descending semantic order (newest first); docs
 * with no `default_release` collect into a final "Unversioned" group.
 * Inside each group docs keep the order they came in (the API already
 * sorts by updated_at), so the caller doesn't need to re-sort. */
export function groupByRelease(docs: DocSummary[]): ReleaseGroup[] {
  const buckets = new Map<string, DocSummary[]>();
  const UNVERSIONED = "__unversioned__";
  for (const d of docs) {
    const key = d.default_release?.version ?? UNVERSIONED;
    const arr = buckets.get(key);
    if (arr) arr.push(d);
    else buckets.set(key, [d]);
  }
  const entries = Array.from(buckets.entries());
  entries.sort((a, b) => {
    if (a[0] === UNVERSIONED) return 1;
    if (b[0] === UNVERSIONED) return -1;
    return compareVersions(b[0], a[0]);
  });
  return entries.map(([key, list]) => ({
    release: key === UNVERSIONED ? null : key,
    name: null,
    docs: list,
  }));
}
